/**
 * useChoreographyStore — pinia singleton for completion-choreography signals.
 *
 * Shared state between the meter renderer (the canvas-side `playCompletion`
 * choreography) and the chassis (the `data-engine-state` cascade owner).
 * The renderer signals `markChoreographyComplete()` after the 2.5s whirl
 * + 550ms settle resolves; `useEngineState` reads `choreographyComplete`
 * to gate the chassis transition from `running` to `complete`.
 *
 * AJ-W1-θ Path θ-A.1.a — without this gate, the chassis flips to
 * `data-engine-state="complete"` the instant the last metric flips to
 * `TestState.finished` (the upload-final progress tick); the running
 * layout (meter side-by-side with metric column) collapses and the
 * complete-wrap fades in WHILE the meter dial is still playing its
 * 2.5s farewell whirl. The user reads the orphaned whirl as
 * "blank/errored" because the surrounding context has already moved on.
 *
 * With the gate, the meter's whirl IS the running state's farewell
 * choreography — the chassis stays in `running` for the duration of the
 * whirl, then the renderer signals completion and the chassis advances.
 * The meter's playCompletion has a natural visual home (its own slot in
 * the running layout) for its entire duration.
 *
 * Lifecycle:
 *  - `resetChoreography()` — called on `isRunning` rising edge (engine
 *    start) so a re-test re-arms the signal.
 *  - `markChoreographyComplete()` — called by the meter renderer once
 *    `playCompletion` resolves.
 *  - PRM-safe: an `<prefers-reduced-motion>` user already gets the
 *    1ms transition durations; the gate adds no perceived delay because
 *    the meter renderer signals immediately after its near-instant
 *    PRM completion path.
 */

import { defineStore } from "pinia";
import { ref, type Ref } from "vue";

export const useChoreographyStore = defineStore("choreography", () => {
    /**
     * True once the meter's `playCompletion` whirl + settle has fully
     * resolved (or the engine never started a choreography this run).
     * Defaults to `true` so the idle state's `useEngineState` reads
     * `idle` (the `allComplete` short-circuit only fires when ALL
     * metrics are `finished`, so the default-true is safe at boot).
     *
     * The engine's `start()` flips this to `false` at the rising edge so
     * the gate arms; the meter renderer flips it back to `true` at the
     * resolution of `playCompletion`.
     */
    const choreographyComplete: Ref<boolean> = ref(true);

    /** Arm the gate at the start of a fresh run. */
    function resetChoreography(): void {
        choreographyComplete.value = false;
    }

    /** The meter renderer's signal that `playCompletion` has resolved. */
    function markChoreographyComplete(): void {
        choreographyComplete.value = true;
    }

    return {
        choreographyComplete,
        resetChoreography,
        markChoreographyComplete,
    };
});
